import { useEffect, useRef, useState } from 'react';
import { CalendarClock } from 'lucide-react';
import Card from '../../../components/ui/Card';
import { useFeature } from '../../../stores/featureStore';
import { useClassroomStore } from '../store';
import { useAttendanceStore } from '../../attendance/store';
import { getToday, getSessions } from '../classroomApi';
import type { ClassSession } from '../classroomApi';

// /class redesign (2026-09-08) — an instructor opened the page after the
// first real class and had no idea how to use it. The reason: the live poll
// behind LiveActivity (../store.ts) only ever carries a session once it's
// LIVE with an activity released, so every other moment — no class today,
// today's class not started, class live but between activities — rendered
// nothing at all. This banner covers those moments; once an activity is
// live, LiveActivity below takes over and this steps back to one framing
// line instead of repeating it.

// Tri-state: `undefined` = not fetched yet, `null` = fetched, nothing found.
type Loadable<T> = T | null | undefined;

function localTodayISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function fmtDate(d: string | null): string {
  if (!d) return 'not yet dated';
  // DATEONLY strings ('YYYY-MM-DD') parsed as a local calendar date —
  // `new Date(d)` reads it as UTC midnight and can print the wrong weekday
  // west of UTC (same fix as pages/teach/TeachSessions.tsx's fmtDate).
  const [y, m, day] = d.split('-').map(Number);
  return new Date(y, m - 1, day).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
}

function StatusBadge({ label, tone }: { label: string; tone: 'live' | 'idle' }) {
  return (
    <span
      className={`inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${
        tone === 'live' ? 'bg-ws-danger/10 text-ws-danger' : 'bg-ws-surface-2 text-ws-ink-3'
      }`}
    >
      {label}
    </span>
  );
}

function nextMeetingLine(next: Loadable<ClassSession>): string {
  if (next === undefined) return 'Checking the schedule…';
  if (next === null) return 'No more meetings are scheduled yet.';
  return `Next meeting: ${next.label} on ${fmtDate(next.scheduled_date)}.`;
}

/** The "what state is class in right now" explainer for /class. Renders in
 * every state — including "nothing is happening" — so the page never looks
 * broken. Reads its own state (today's session + the shared live poll),
 * takes no props. */
export function ClassPageStatusBanner() {
  const enabled = useFeature('class_activities');
  const liveSession = useClassroomStore((s) => s.session);
  const liveActivity = useClassroomStore((s) => s.activity);
  // 2026-09 rate-limit incident — a poll that keeps failing must never look
  // identical to "nothing is live yet". `unreachable` is only true after
  // repeated failures (see store.ts), so one dropped poll doesn't flash this.
  const unreachable = useClassroomStore((s) => s.unreachable);
  const attendanceEnabled = useFeature('class_attendance');
  const attendanceWindow = useAttendanceStore((s) => s.window);

  const [today, setToday] = useState<Loadable<ClassSession>>(undefined);
  const [next, setNext] = useState<Loadable<ClassSession>>(undefined);
  const wasLive = useRef(false);

  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    getToday()
      .then((s) => { if (alive) setToday(s); })
      .catch(() => { if (alive) setToday(null); });
    return () => { alive = false; };
  }, [enabled]);

  // A live session just ended — re-check "today" so the banner flips from
  // "live" to "ended" / "next meeting" instead of freezing on stale state.
  useEffect(() => {
    if (liveSession) { wasLive.current = true; return; }
    if (!wasLive.current || !enabled) return;
    wasLive.current = false;
    getToday().then(setToday).catch(() => setToday(null));
  }, [liveSession, enabled]);

  useEffect(() => {
    const needsNext = today === null || today?.status === 'closed';
    if (!enabled || !needsNext) return;
    let alive = true;
    const todayIso = localTodayISO();
    getSessions()
      .then((sessions) => {
        if (!alive) return;
        const upcoming = sessions
          .filter((s): s is ClassSession & { scheduled_date: string } => !!s.scheduled_date && s.scheduled_date > todayIso)
          .sort((a, b) => (a.scheduled_date < b.scheduled_date ? -1 : 1));
        setNext(upcoming[0] ?? null);
      })
      .catch(() => { if (alive) setNext(null); });
    return () => { alive = false; };
  }, [enabled, today]);

  if (!enabled) return null;

  // Activity is live — LiveActivity (below, same feature) already renders
  // the prompt; add one line of framing so a first-time student knows to
  // look there, without repeating what it says.
  if (liveSession && liveActivity) {
    return (
      <p className="flex flex-wrap items-center gap-2 text-sm text-ws-ink-2">
        <StatusBadge label="Live" tone="live" />
        An activity is live below — read the prompt and respond when you&rsquo;re ready.
        {unreachable && (
          <span className="text-ws-ink-3">(Having trouble staying in sync — retrying…)</span>
        )}
      </p>
    );
  }

  // The poll itself is failing — this is NOT "no class today" or "not
  // started yet"; those readings come from the same poll and can't be
  // trusted right now either. Shown before every other idle state so a
  // failing connection never gets silently mistaken for "waiting for
  // instructor" (the 2026-09 incident: a class of 77 whose polls were
  // rejected by the rate limiter sat on that exact stale banner all period).
  if (unreachable) {
    return (
      <Card className="p-4 sm:p-5">
        <div className="flex items-center gap-2">
          <StatusBadge label="Reconnecting" tone="idle" />
          <h2 className="text-sm font-semibold text-ws-ink">Can&rsquo;t reach the class page</h2>
        </div>
        <p className="mt-2 text-sm text-ws-ink-2">
          We&rsquo;re having trouble reaching the server right now — this is not
          the same as no class being live. Retrying automatically; no need to
          reload.
        </p>
      </Card>
    );
  }

  // Class is live, instructor hasn't released an activity yet.
  if (liveSession) {
    return (
      <Card className="p-4 sm:p-5">
        <div className="flex items-center gap-2">
          <StatusBadge label="Live" tone="live" />
          <h2 className="text-sm font-semibold text-ws-ink">{liveSession.label}</h2>
        </div>
        <p className="mt-2 text-sm text-ws-ink-2">
          {attendanceEnabled && attendanceWindow
            ? 'Attendance check-in is open above. Activities will appear here as your instructor releases them.'
            : 'Class is underway. Activities will appear here as your instructor releases them — nothing to do yet.'}
        </p>
      </Card>
    );
  }

  if (today === undefined) return null; // first load — avoid a flash of "no class today"

  if (today && today.status === 'planned') {
    return (
      <Card className="p-4 sm:p-5">
        <div className="flex items-center gap-2">
          <StatusBadge label="Not started" tone="idle" />
          <h2 className="text-sm font-semibold text-ws-ink">{today.label}</h2>
        </div>
        <p className="mt-2 text-sm text-ws-ink-2">
          Today&rsquo;s meeting hasn&rsquo;t started yet. Check-in{today.activity_expected ? ' and any activities' : ''} will
          appear here as soon as your instructor starts class.
        </p>
      </Card>
    );
  }

  if (today && today.status === 'closed') {
    return (
      <Card className="p-4 sm:p-5">
        <div className="flex items-center gap-2">
          <StatusBadge label="Ended" tone="idle" />
          <h2 className="text-sm font-semibold text-ws-ink">{today.label}</h2>
        </div>
        <p className="mt-2 text-sm text-ws-ink-2">Today&rsquo;s meeting has ended. {nextMeetingLine(next)}</p>
      </Card>
    );
  }

  // No meeting scheduled today at all.
  return (
    <Card className="p-4 sm:p-5">
      <div className="flex items-center gap-2">
        <CalendarClock size={16} className="text-ws-ink-3" />
        <h2 className="text-sm font-semibold text-ws-ink">No class today</h2>
      </div>
      <p className="mt-2 text-sm text-ws-ink-2">
        {nextMeetingLine(next)} Check-in and any activities will appear here once that meeting starts.
      </p>
    </Card>
  );
}

export default ClassPageStatusBanner;
